/**
 * Usage Report Pulse Templates
 * Based on best practices from Mixpanel, Amplitude, Datadog
 */

import { PulseReportTemplate } from "./types";

export const WEEKLY_USAGE: PulseReportTemplate = {
  id: "weekly-usage",
  name: "Weekly Usage Report",
  reportType: "usage",
  description: "Weekly summary of chat usage and consumption metrics",
  purpose:
    "Keep users informed about their weekly consumption. Builds habit of checking reports and prevents surprise overages.",
  icon: "BarChartOutlined",
  color: "#0ea5e9",
  suggestedHtmlTemplateId: "usage-report",
  suggestedSubject: "Your Weekly Usage Report - {{usage_chats_used}} chats used",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1, // Monday
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "All active users",
  expectedOutcomes: [
    "50%+ email open rate",
    "Increased platform engagement",
    "Proactive usage management",
    "Reduced overage surprises",
  ],
  bestPractices: [
    "Send at the start of the week",
    "Show week-over-week comparison",
    "Include visual usage progress bar",
    "Highlight remaining allowance clearly",
  ],
  keyMetrics: [
    "Chats used this week",
    "Remaining allowance",
    "Week-over-week change",
    "Storage used",
    "Peak usage day",
  ],
  tags: ["usage", "weekly", "metrics", "consumption", "report"],
  bestFor: ["Usage-based pricing", "Freemium models", "Quota monitoring"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Clean weekly usage digest with progress visualization",
};

export const USAGE_LIMIT_WARNING: PulseReportTemplate = {
  id: "usage-limit-warning",
  name: "Usage Limit Warning",
  reportType: "usage",
  description: "Alert when approaching usage limits",
  purpose:
    "Warn users before they hit limits. Opportunity to upsell while preventing service disruption.",
  icon: "AlertOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "usage-limit-warning",
  suggestedSubject: "Heads up: {{usage_percentage}}% of your chat limit used",
  defaultSchedule: {
    type: "on_event",
    eventType: "usage_threshold_80",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Users at 80%+ of their usage limit",
  expectedOutcomes: [
    "30%+ upgrade conversion from warning",
    "Reduced service interruptions",
    "Proactive customer communication",
    "Increased upgrade revenue",
  ],
  bestPractices: [
    "Trigger at 80% usage threshold",
    "Show estimated days until depleted",
    "Provide clear upgrade path",
    "Include usage optimization tips",
  ],
  keyMetrics: [
    "Current usage percentage",
    "Remaining chats",
    "Estimated depletion date",
    "Upgrade options",
    "Usage trend",
  ],
  tags: ["usage", "warning", "limit", "threshold", "upgrade"],
  bestFor: ["Usage-based plans", "Freemium tiers", "Consumption alerts"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Attention-grabbing warning with upgrade CTA",
};

export const MONTHLY_USAGE: PulseReportTemplate = {
  id: "monthly-usage",
  name: "Monthly Usage Summary",
  reportType: "usage",
  description: "Comprehensive monthly usage report with trends",
  purpose:
    "End-of-month comprehensive view. Perfect for budget planning and usage optimization.",
  icon: "CalendarOutlined",
  color: "#6366f1",
  suggestedHtmlTemplateId: "usage-report",
  suggestedSubject: "Your {{current_month}} Usage Summary",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "All active users at month end",
  expectedOutcomes: [
    "Clear monthly consumption visibility",
    "Better budget planning for customers",
    "Identification of usage patterns",
    "Data-driven upgrade decisions",
  ],
  bestPractices: [
    "Send on the 1st of each month",
    "Include month-over-month comparison",
    "Break down by chatbot/feature",
    "Show cost per conversation if relevant",
  ],
  keyMetrics: [
    "Total monthly usage",
    "Month-over-month change",
    "Most used features",
    "Cost breakdown",
    "Usage by chatbot",
  ],
  tags: ["usage", "monthly", "comprehensive", "trends", "summary"],
  bestFor: ["Enterprise accounts", "Budget tracking", "Usage optimization"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Detailed monthly usage with trend analysis",
};

export const USAGE_OVERAGE: PulseReportTemplate = {
  id: "usage-overage",
  name: "Usage Overage Alert",
  reportType: "usage",
  description: "Notification when usage exceeds plan limits",
  purpose:
    "Immediate alert when users exceed limits. Transparent about overage charges.",
  icon: "ExclamationCircleOutlined",
  color: "#dc2626",
  suggestedHtmlTemplateId: "usage-limit-warning",
  suggestedSubject: "You've exceeded your {{billing_plan}} limit",
  defaultSchedule: {
    type: "on_event",
    eventType: "usage_threshold_100",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Users who have exceeded their usage limits",
  expectedOutcomes: [
    "Transparent overage communication",
    "50%+ immediate upgrade conversion",
    "Reduced billing disputes",
    "Clear action path for users",
  ],
  bestPractices: [
    "Send immediately when limit exceeded",
    "Be transparent about overage pricing",
    "Show current overage amount",
    "Provide instant upgrade option",
  ],
  keyMetrics: [
    "Current overage amount",
    "Overage rate",
    "Recommended upgrade plan",
    "Savings with upgrade",
    "Current bill projection",
  ],
  tags: ["usage", "overage", "exceeded", "urgent", "billing"],
  bestFor: ["Pay-as-you-go plans", "Overage management", "Usage caps"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Clear overage notification with upgrade path",
};

export const USAGE_TEMPLATES = [
  WEEKLY_USAGE,
  USAGE_LIMIT_WARNING,
  MONTHLY_USAGE,
  USAGE_OVERAGE,
];
